import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Input,
  Breadcrumb,
  Tag,
  DatePicker,
  Row,
  Col,
  Grid,
  message,
  Card,
  Statistic,
  Space,
  Dropdown,
  Typography,
  Avatar,
  Tooltip,
  Empty,
  Segmented,
  Popconfirm
} from 'antd';
import { Link } from 'react-router-dom';
import {
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  RightOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  TrophyOutlined,
  RiseOutlined,
  ReloadOutlined,
  MoreOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  GiftOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { promotionsServices } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.locale('fr');

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// Hook debounce
const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// Couleurs pour les avatars
const avatarColors = [
  '#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068',
  '#108ee9', '#eb2f96', '#722ed1', '#13c2c2', '#fa541c'
];

const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  const charCode = name.charCodeAt(0);
  return avatarColors[charCode % avatarColors.length];
};

const HistoriquePromotions = () => {
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalActivations: 0,
    activationsToday: 0,
    totalPointsDistributed: 0,
    uniqueDrivers: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('jour');

  // Filtres
  const [dateRange, setDateRange] = useState(null);
  const [query, setQuery] = useState('');
  const queryDebounced = useDebounce(query, 400);

  // Pagination & tri
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [sorter, setSorter] = useState({ field: 'date', order: 'descend' });
  const [resettingId, setResettingId] = useState(null);

  // Breakpoints
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // Params API
  const apiParams = useMemo(() => {
    const params = {
      page,
      limit,
      sort: sorter?.field
        ? (sorter.order === 'descend' ? `-${Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field}`
          : `${Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field}`)
        : '-date',
    };

    if (queryDebounced) params.q = queryDebounced.trim();
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.start = dateRange[0].format('YYYY-MM-DD');
      params.end = dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [page, limit, sorter, queryDebounced, dateRange]);

  // Charger les stats
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const res = await promotionsServices.statsPromotionsDimanche(statsPeriod);
      if (res) {
        setStats({
          totalActivations: res.totalActivations || res.total || 0,
          activationsToday: res.activationsToday || res.count || 0,
          totalPointsDistributed: res.totalPointsDistributed || res.pointsTotal || 0,
          uniqueDrivers: res.uniqueDrivers || res.chauffeurs || 0
        });
      }
    } catch (err) {
      console.error('Erreur stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Charger les données
  const load = async () => {
    try {
      setLoading(true);
      const res = await promotionsServices.listeDesChauffeursQuiOntActivePromoDimanche(apiParams);
      setData(Array.isArray(res?.items) ? res.items : []);
      setTotal(res?.total || 0);

      if (res?.page && res.page !== page) setPage(res.page);
      if (res?.limit && res.limit !== limit) setLimit(res.limit);
    } catch (err) {
      console.error('Erreur API:', err);
      message.error("Impossible de charger l'historique des promotions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, [statsPeriod]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiParams]);

  // Réinitialiser la règle de travail d'un chauffeur
  const handleResetRegle = async (contractorId) => {
    try {
      setResettingId(contractorId);
      const res = await promotionsServices.resetRegleDimancheChauffeur(contractorId);
      if (res?.success) {
        message.success(res.message || 'Règle réinitialisée avec succès.');
        load(); // Actualiser le tableau
      } else {
        message.error(res?.message || 'Erreur lors de la réinitialisation.');
      }
    } catch (err) {
      console.error('Erreur reset:', err);
      message.error('Erreur lors de la réinitialisation de la règle.');
    } finally {
      setResettingId(null);
    }
  };

  // Format téléphone CI
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let digits = String(phone).replace(/\D/g, '');
    if (digits.startsWith('225')) digits = digits.slice(3);
    if (digits.length === 9) digits = '0' + digits;
    return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  // Format nombre
  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num || 0);
  };

  // Exports
  const exportRows = data.map((item) => ({
    Nom: `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim(),
    Téléphone: formatPhoneNumber(item.user?.phone),
    Points: Number(item.user?.soldePoints?.point ?? 0),
    Date: dayjs(item.date).format('DD/MM/YYYY HH:mm:ss'),
  }));

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Promotions Dimanche');
    XLSX.writeFile(wb, `promotions_dimanche_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    message.success('Export Excel réussi !');
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `promotions_dimanche_${dayjs().format('YYYY-MM-DD')}.csv`);
    message.success('Export CSV réussi !');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Historique Promotions Dimanche', 14, 22);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Généré le ${dayjs().format('DD/MM/YYYY à HH:mm')}`, 14, 30);

    autoTable(doc, {
      startY: 38,
      head: [['Nom', 'Téléphone', 'Points', 'Date d\'activation']],
      body: data.map((item) => [
        `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim(),
        formatPhoneNumber(item.user?.phone),
        formatNumber(Number(item.user?.soldePoints?.point ?? 0)),
        dayjs(item.date).format('DD/MM/YYYY HH:mm'),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [245, 130, 32] },
    });
    doc.save(`promotions_dimanche_${dayjs().format('YYYY-MM-DD')}.pdf`);
    message.success('Export PDF réussi !');
  };

  const exportMenuItems = [
    {
      key: 'excel',
      label: 'Excel (.xlsx)',
      icon: <FileExcelOutlined style={{ color: '#217346' }} />,
      onClick: exportToExcel,
    },
    {
      key: 'csv',
      label: 'CSV',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      onClick: exportToCSV,
    },
    {
      key: 'pdf',
      label: 'PDF',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      onClick: exportToPDF,
    },
  ];

  const columns = [
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      render: (_, record) => {
        const fullName = `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim() || 'Inconnu';
        const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              style={{ backgroundColor: getAvatarColor(fullName), flexShrink: 0 }}
              size={isMobile ? 32 : 40}
            >
              {initials || <UserOutlined />}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ display: 'block', fontSize: isMobile ? 13 : 14 }}>
                {fullName}
              </Text>
              {isMobile && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {formatPhoneNumber(record.user?.phone)}
                </Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Téléphone',
      key: 'phone',
      dataIndex: ['user', 'phone'],
      render: (v) => (
        <Text copyable={{ text: v }} style={{ fontFamily: 'monospace' }}>
          {formatPhoneNumber(v)}
        </Text>
      ),
      responsive: ['md'],
    },
    {
      title: 'Solde Points',
      key: 'points',
      dataIndex: ['user', 'soldePoints', 'point'],
      align: 'right',
      render: (points) => {
        const n = Number(points ?? 0);
        const color = n >= 1000 ? 'gold' : n >= 100 ? 'green' : n >= 0 ? 'blue' : 'red';
        return (
          <Tag
            color={color}
            style={{
              fontWeight: 600,
              fontSize: isMobile ? 12 : 13,
              padding: '4px 12px',
              borderRadius: 20,
            }}
          >
            {formatNumber(n)} pts
          </Tag>
        );
      },
      responsive: ['sm'],
    },
    {
      title: 'Règle de travail',
      key: 'regleTravail',
      align: 'center',
      responsive: ['sm'],
      render: (_, record) => {
        const regleId = record.user?.regleTravailId || '';
        const yahweRuleDefault = '656cbf2ed4e7406fa78ec2107ec9fefe';
        const yahweRuleSPD = '2093df3768604963a1f7f898a94e23bf';
        const isYahwe = regleId === yahweRuleDefault || regleId === yahweRuleSPD;
        return (
          <Tag
            color={isYahwe ? 'volcano' : 'green'}
            style={{ fontWeight: 600, borderRadius: 20, padding: '2px 12px' }}
          >
            {isYahwe ? 'YAHWE' : regleId ? 'Normale' : 'Non définie'}
          </Tag>
        );
      },
    },
    {
      title: 'Date d\'activation',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (text) => (
        <Tooltip title={dayjs(text).format('dddd DD MMMM YYYY à HH:mm:ss')}>
          <Space size={4}>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
            <span>{dayjs(text).format(isMobile ? 'DD/MM/YY' : 'DD/MM/YYYY HH:mm')}</span>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const fullName = `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim() || 'ce chauffeur';
        return (
          <Popconfirm
            title="Réinitialiser la règle de travail"
            description={`Remettre ${fullName} sur la règle normale ?`}
            onConfirm={() => handleResetRegle(record.contractorId)}
            okText="Oui, réinitialiser"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Réinitialiser la règle de travail normale">
              <Button
                type="text"
                icon={<UndoOutlined />}
                loading={resettingId === record.contractorId}
                style={{ color: '#fa8c16' }}
              >
                {!isMobile && 'Réinitialiser'}
              </Button>
            </Tooltip>
          </Popconfirm>
        );
      },
    },
  ];

  // Stats cards configuration
  const statsCards = [
    {
      title: 'Total Activations',
      value: stats.totalActivations,
      icon: <TrophyOutlined />,
      color: '#f58220',
      bgColor: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
    },
    {
      title: 'Chauffeurs Uniques',
      value: stats.uniqueDrivers,
      icon: <UserOutlined />,
      color: '#722ed1',
      bgColor: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
    },
    {
      title: 'Points Distribués',
      value: stats.totalPointsDistributed,
      icon: <GiftOutlined />,
      color: '#52c41a',
      bgColor: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
      suffix: 'pts',
    },
    {
      title: 'Aujourd\'hui',
      value: stats.activationsToday,
      icon: <RiseOutlined />,
      color: '#1890ff',
      bgColor: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
    },
  ];

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <span>Promotion</span> },
          { title: <span style={{ fontWeight: 600 }}>Promo Dimanche</span> },
        ]}
      />

      {/* Header avec titre */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <TrophyOutlined style={{ color: '#f58220' }} />
            Promotion Dimanche
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Historique des activations de la promotion dimanche
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={() => { load(); loadStats(); }}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button type="primary" icon={<DownloadOutlined />}>
              Exporter {!isMobile && <MoreOutlined />}
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/* Statistiques Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsCards.map((card, index) => (
          <Col xs={12} sm={12} md={6} key={index}>
            <Card
              size="small"
              loading={statsLoading}
              style={{
                background: card.bgColor,
                border: 'none',
                borderRadius: 12,
                height: '100%',
              }}
              bodyStyle={{ padding: isMobile ? '16px 12px' : '20px 16px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 4 }}>
                    {card.title}
                  </Text>
                  <Statistic
                    value={card.value}
                    suffix={card.suffix}
                    valueStyle={{
                      fontSize: isMobile ? 20 : 28,
                      fontWeight: 700,
                      color: card.color,
                    }}
                  />
                </div>
                <div
                  style={{
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48,
                    borderRadius: 12,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? 18 : 22,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card
        style={{ marginBottom: 24, borderRadius: 12 }}
        bodyStyle={{ padding: isMobile ? 16 : 20 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10} lg={8}>
            <RangePicker
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={dateRange}
              onChange={(val) => { setPage(1); setDateRange(val); }}
              placeholder={['Date début', 'Date fin']}
              allowClear
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} md={14} lg={10}>
            <Input
              size={isMobile ? 'middle' : 'large'}
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom ou téléphone..."
              value={query}
              onChange={(e) => { setPage(1); setQuery(e.target.value); }}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} lg={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                Période stats:
              </Text>
              <Segmented
                size={isMobile ? 'small' : 'middle'}
                options={[
                  { label: 'Jour', value: 'jour' },
                  { label: 'Semaine', value: 'semaine' },
                  { label: 'Mois', value: 'mois' },
                ]}
                value={statsPeriod}
                onChange={setStatsPeriod}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        <Table
          loading={isLoading}
          size={isMobile ? 'small' : 'middle'}
          columns={columns}
          dataSource={data}
          rowKey={(record, idx) => record._id || `${record.contractorId}-${record.date}-${idx}`}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {`${range[0]}-${range[1]} sur ${total} résultats`}
              </Text>
            ),
            style: { padding: '12px 16px', margin: 0 },
          }}
          onChange={(pagination, _filters, sorterArg) => {
            setPage(pagination.current);
            setLimit(pagination.pageSize);
            const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
            if (s && s.field) {
              setSorter({ field: s.field, order: s.order || 'descend' });
            } else {
              setSorter({ field: 'date', order: 'descend' });
            }
          }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    Aucune activation trouvée pour cette période
                  </Text>
                }
              />
            ),
          }}
          rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
      </Card>

      {/* Styles inline pour les rows alternées */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #fff7e6 !important;
        }
        .ant-table-thead > tr > th {
          background-color: #fafafa !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        .ant-pagination {
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  );
};

export default HistoriquePromotions;
